<script setup lang="ts">
import { computed } from 'vue'
import { useRouter } from 'vue-router'
import { useAuthStore } from '@/stores/auth'
import { authService } from '@/services/auth'
import { tokenManager } from '@/services/tokenManager'
import { sanitizeUserName, sanitizeEmail } from '@/utils/sanitize'

const router = useRouter()
const authStore = useAuthStore()

// Computed properties with sanitization
const isAuthenticated = computed(() => authStore.isAuthenticated)
const userDisplayName = computed(() => sanitizeUserName(authStore.userDisplayName))
const userEmail = computed(() => sanitizeEmail(authStore.user?.email))

/**
 * Handle user logout
 */
async function handleLogout() {
  try {
    // Clear tokens from storage
    await tokenManager.clearTokens()
    
    // Clear auth store
    authStore.clearAuth()
    
    // Redirect to Cognito logout (which will redirect back to app)
    authService.logout()
  } catch (error) {
    console.error('Logout error:', error)
    // Even if there's an error, clear local state and redirect
    authStore.clearAuth()
    router.push('/login')
  }
}

/**
 * Navigate to login
 */
function handleLogin() {
  router.push('/login')
}
</script>

<template>
  <nav class="navbar" role="navigation" aria-label="Main navigation">
    <div class="navbar-content">
      <!-- Logo/Title -->
      <div class="navbar-brand">
        <router-link to="/" class="brand-link" aria-label="Green Custard Project Health - Home">
          <h1 class="brand-title">Green Custard Project Health</h1>
        </router-link>
      </div>

      <!-- User Section -->
      <div class="navbar-user" role="region" aria-label="User account">
        <!-- Authenticated User -->
        <div v-if="isAuthenticated" class="user-info">
          <div class="user-details" aria-label="User information">
            <span class="user-name">{{ userDisplayName }}</span>
            <span class="user-email">{{ userEmail }}</span>
          </div>
          
          <button 
            class="logout-button" 
            @click="handleLogout" 
            aria-label="Logout from your account"
          >
            <svg class="logout-icon" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
              <path fill-rule="evenodd" d="M3 3a1 1 0 00-1 1v12a1 1 0 102 0V4a1 1 0 00-1-1zm10.293 9.293a1 1 0 001.414 1.414l3-3a1 1 0 000-1.414l-3-3a1 1 0 10-1.414 1.414L14.586 9H7a1 1 0 100 2h7.586l-1.293 1.293z" clip-rule="evenodd" />
            </svg>
            <span class="logout-text">Logout</span>
          </button>
        </div>

        <!-- Not Authenticated -->
        <div v-else class="login-section">
          <button class="login-button" @click="handleLogin" aria-label="Login to your account">
            <svg class="login-icon" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
              <path fill-rule="evenodd" d="M3 3a1 1 0 011 1v12a1 1 0 11-2 0V4a1 1 0 011-1zm7.707 3.293a1 1 0 010 1.414L9.414 9H17a1 1 0 110 2H9.414l1.293 1.293a1 1 0 01-1.414 1.414l-3-3a1 1 0 010-1.414l3-3a1 1 0 011.414 0z" clip-rule="evenodd" />
            </svg>
            <span>Login</span>
          </button>
        </div>
      </div>
    </div>
  </nav>
</template>

<style scoped>
.navbar {
  background-color: #ffffff;
  border-bottom: 1px solid #e2e8f0;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
  position: sticky;
  top: 0;
  z-index: 100;
}

.navbar-content {
  max-width: 1400px;
  margin: 0 auto;
  padding: 1rem 2rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 2rem;
}

/* Brand */
.navbar-brand {
  flex-shrink: 0;
}

.brand-link {
  text-decoration: none;
  color: inherit;
}

.brand-title {
  font-family: 'Inter', Arial, sans-serif;
  font-weight: 600;
  font-size: 1.25rem;
  color: #2f405b;
  margin: 0;
  transition: color 0.2s ease;
}

.brand-link:hover .brand-title {
  color: #4aa483;
}

/* User Section */
.navbar-user {
  display: flex;
  align-items: center;
  gap: 1rem;
}

/* User Info */
.user-info {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.user-details {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 0.125rem;
}

.user-name {
  font-size: 0.9rem;
  font-weight: 600;
  color: #2d3748;
  line-height: 1.2;
}

.user-email {
  font-size: 0.8rem;
  color: #718096;
  line-height: 1.2;
}

/* Logout Button */
.logout-button {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  font-size: 0.9rem;
  font-weight: 500;
  color: #4a5568;
  background-color: #f7fafc;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s ease;
}

.logout-button:hover {
  background-color: #edf2f7;
  border-color: #cbd5e0;
  color: #2d3748;
}

.logout-button:active {
  background-color: #e2e8f0;
}

.logout-icon {
  width: 18px;
  height: 18px;
}

.logout-text {
  display: inline;
}

/* Login Button */
.login-button {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.625rem 1.25rem;
  font-size: 0.95rem;
  font-weight: 600;
  color: white;
  background: linear-gradient(135deg, #4aa483 0%, #3d8a6d 100%);
  border: none;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s ease;
  box-shadow: 0 2px 8px rgba(74, 164, 131, 0.3);
}

.login-button:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(74, 164, 131, 0.4);
  background: linear-gradient(135deg, #3d8a6d 0%, #307057 100%);
}

.login-button:active {
  transform: translateY(0);
}

.login-icon {
  width: 18px;
  height: 18px;
}

/* Responsive Design */
@media (max-width: 768px) {
  .navbar-content {
    padding: 0.875rem 1rem;
  }

  .brand-title {
    font-size: 1.1rem;
  }

  .user-details {
    display: none;
  }

  .logout-text {
    display: none;
  }

  .logout-button {
    padding: 0.5rem;
  }

  .login-button {
    padding: 0.5rem 1rem;
    font-size: 0.9rem;
  }
}

@media (max-width: 480px) {
  .brand-title {
    font-size: 1rem;
  }
}
</style>
